import { SERP_CLASSIFICATION_LABELS, type MentionSentiment } from './serp-classification.js';
import type { PageContent } from './serp-page.types.js';
export const AI_CATEGORIES = [
  'competitor',
  'about',
  'namesake',
  'unrelated',
  'unreviewed',
] as const;
export type AiCategory = (typeof AI_CATEGORIES)[number];
export const AI_CATEGORY_LABELS = Object.fromEntries(
  AI_CATEGORIES.map((key) => [key, SERP_CLASSIFICATION_LABELS[key]]),
) as Record<AiCategory, string>;
export interface AiSuggestion {
  category: AiCategory;
  sentiment: MentionSentiment | null;
  reason: string;
  quotes: Array<{ field: keyof Omit<PageContent, 'textTruncated'>; text: string }>;
}
export interface AiEvidence {
  url: string;
  finalUrl: string;
  brandBrief: string;
  page: PageContent;
  captureId: string;
  contentHash: string;
  reviewVersion: string;
  profileRevision: number;
}
export interface AiProfile {
  brief: string;
  revision: number;
}
export interface AiSettings {
  enabled: boolean;
  model: string;
  profile: AiProfile;
  budget: {
    limitUsd: number;
    accountedUsd: number;
    reservedUsd: number;
    uncertainUsd: number;
    remainingUsd: number;
    maxPerUrlUsd: number;
  };
}
export type AiStatus = 'preparing' | 'processing' | 'ready' | 'failed' | 'uncertain';
export interface AiProposal {
  id: string;
  resultId: string;
  url: string;
  status: AiStatus;
  model: string;
  createdAt: string;
  finishedAt: string | null;
  error: string | null;
  suggestion: AiSuggestion | null;
  evidence: AiEvidence | null;
  review: {
    action: 'accepted' | 'corrected' | 'rejected';
    actorId: string;
    at: string;
    category: AiCategory | null;
    sentiment: MentionSentiment | null;
    reason: string;
  } | null;
  stale: string | null;
  costUsd: number;
  costUncertain: boolean;
  usage: { inputTokens: number; outputTokens: number } | null;
}
export interface AiProposalPage {
  active?: boolean;
  items: AiProposal[];
  page: number;
  hasMore: boolean;
}

export interface AiSelectionScope {
  groupId: string;
  runId?: string;
  top: number;
  changes?: 'changed';
}
export interface AiSelectionPlan {
  fingerprint: string;
  total: number;
  queryCount: number;
  label: string;
  maxReserveUsd: number;
}
export interface AiBatch {
  id: string;
  label: string;
  status: 'queued' | 'complete' | 'stopped' | 'failed';
  total: number;
  submitted: number;
  skipped: number;
  preparing: number;
  processing: number;
  ready: number;
  failed: number;
  reviewed: number;
  error: string | null;
  createdAt: string;
}
export interface AiBulkReview {
  accepted: string[];
  skipped: Array<{ id: string; reason: string }>;
}
export const AI_REVIEW_LIMIT = 50;
export const canAcceptAiProposal = (row: AiProposal) =>
  row.status === 'ready' &&
  !row.review &&
  !row.stale &&
  Boolean(row.suggestion && row.suggestion.category !== 'unreviewed');
